// src/oura/connectors.ts
//
// Data-source connectors, per business. Distinct from contract.ts `Connection`
// (publish-access — how ourteam pushes a fix onto a client property). These are
// the third-party accounts Oura reads stats from, or publishes through:
//
//   Search & analytics → GSC, GA4, Bing Webmaster (impressions, traffic, index)
//   Website platform    → WordPress, Webflow, Wix, Shopify, Squarespace (publish)
//   Business profile     → Google Business Profile (listings, reviews)
//   Client documents     → Google Docs (status only: Oura reads the client's
//                          Drive folder as oura@ourteam.ai, so nobody connects it)
//
// Mock seam shaped like the future GET /assessments/{id}/connectors. Real wiring
// is OAuth (Google/Bing/Shopify) + an application password / plugin (WordPress),
// tokens stored server-side and never in the client bundle — see the BE ticket.

import type { DocsConnection, DocsFolder } from "@service/aeo/clientDocs";
import type { Business } from "./contract";
import type { DocsLoad } from "./docs/hooks";
import { agoText, docsFailureText, isReadStale } from "./docs/format";
import { FOLDER_STATUS, folderErrorStatus, type Tone } from "./ui/tokens";

/**
 * Every key the BE catalog can send. This union is the FE's copy of a list the
 * backend owns, so it drifts the moment one side ships a connector — which is
 * exactly what happened: `framer` went live in the BE catalog
 * (ourteam-backend#3801) and was rendered here as a blank tile for weeks,
 * because nothing typed the gap. `connectorLogos.tsx` now fails to compile if a
 * key is added here without a mark, and falls back to a readable placeholder if
 * the BE sends one this list has never heard of.
 *
 * `gbp` stays even though #3801 dropped it from the catalog: an older BE that
 * still returns it must keep degrading gracefully, and the cost of the extra
 * member is one switch case.
 */
export type ConnectorKey =
    | "gsc" | "ga4" | "bing"
    | "wordpress" | "framer" | "webflow" | "wix" | "shopify" | "squarespace"
    | "gbp"
    | "google_docs";
export type ConnectorCategory = "search" | "cms" | "profile" | "docs";
export type ConnectorStatus = "connected" | "action_needed" | "not_connected";

export interface DataConnector {
    key: ConnectorKey;
    label: string;
    category: ConnectorCategory;
    blurb: string; // one line: what connecting it unlocks
    status: ConnectorStatus;
    detail: string; // account / property line, or why it needs attention
    lastSync: string | null; // ISO date of the last successful pull
    /**
     * The property this connector is actually bound to, e.g.
     * `sc-domain:example.com`. Optional: proposed in the Sprint 0 handoff and
     * not shipped by every environment yet.
     */
    propertyUrl?: string | null;
    /**
     * The BE compared the connected property against the host we crawl and
     * they differ. **Absent means not validated**, which is not the same as
     * valid — read it only on an explicit `=== true` (see gscState.ts).
     */
    propertyMismatch?: boolean;
    /**
     * Which draft types this platform can actually publish
     * (ourteam-backend#4761). WordPress returns all four; Wix returns
     * `meta_description` and `schema_jsonld`, because a Wix page body is not
     * addressable HTML and an FAQ block has nowhere to go.
     *
     * **Absent means unknown, not none** — the same rule `propertyMismatch`
     * keeps above. An older backend sends no such field, and reading that as
     * "publishes nothing" would hide every Publish button in the product on the
     * day this shipped. Read it only when it is actually there.
     */
    publishableTypes?: string[];
}

/**
 * How each platform writes its own name.
 *
 * The wire sends the lowercase key (`wordpress`), and printing that verbatim is
 * how the Workshop's History ended up reading "published to wordpress" while
 * the publish response for the same publication said "WordPress" (#3547). The
 * FE owns the display name so the two can't disagree, whatever the wire sends.
 */
/**
 * A draft type as a person would say it, in the plural, so it reads inside a
 * sentence: "Wix has no way to publish FAQ blocks."
 *
 * Same reason CONNECTOR_LABEL exists below — the wire sends `faq_block`, and
 * printing that verbatim, or lowercasing a label to fit a sentence, is how
 * "FAQ block" became "faq block". An unrecognised type degrades to its own key
 * with the underscores removed: a fifth type added by the backend reads
 * awkwardly rather than not at all.
 */
export function publishableTypeLabel(key: string): string {
    const LABELS: Record<string, string> = {
        meta_description: "meta descriptions",
        schema_jsonld: "structured data",
        faq_block: "FAQ blocks",
        internal_link: "internal links",
        alt_text: "image descriptions",
        page_copy: "body content",
    };
    return LABELS[key] ?? key.replace(/_/g, " ");
}

export const CONNECTOR_LABEL: Record<ConnectorKey, string> = {
    gsc: "Google Search Console",
    ga4: "Google Analytics 4",
    bing: "Bing Webmaster Tools",
    wordpress: "WordPress",
    framer: "Framer",
    webflow: "Webflow",
    wix: "Wix",
    shopify: "Shopify",
    squarespace: "Squarespace",
    gbp: "Google Business Profile",
    google_docs: "Google Docs",
};

/**
 * Display name for a connector key that arrived as a plain string.
 *
 * Unknown keys are title-cased rather than dropped: a platform we haven't
 * catalogued yet still published someone's page, and "Ghost" reads better than
 * both "ghost" and a blank.
 */
export function connectorLabel(raw: string | null | undefined): string {
    if (!raw) return "";
    const key = raw.trim().toLowerCase();
    return (
        CONNECTOR_LABEL[key as ConnectorKey]
        ?? key.replace(/\b\w/g, (c) => c.toUpperCase())
    );
}

/** A connector key we have a logo and a label for, or null if we don't. */
export function asConnectorKey(raw: string | null | undefined): ConnectorKey | null {
    if (!raw) return null;
    const key = raw.trim().toLowerCase();
    return key in CONNECTOR_LABEL ? (key as ConnectorKey) : null;
}

export const CATEGORY_META: Record<ConnectorCategory, { label: string; description: string }> = {
    search: { label: "Search & analytics", description: "Read impressions, organic traffic and indexed pages." },
    cms: { label: "Website platform", description: "Let ourteam publish approved fixes straight to the site." },
    profile: { label: "Business profile", description: "Keep listings, hours and reviews consistent everywhere." },
    docs: { label: "Client documents", description: "Read the client's docs in Drive and the comments on them." },
};

/* ------------------------------------------------------------------------- *
 * Google Docs: a status-only connector
 * ------------------------------------------------------------------------- */

/**
 * Connectors with no Connect or Disconnect. Google Docs runs as
 * oura@ourteam.ai through ourteam's own Google account, so neither staff nor
 * the client connects anything (the backend's connect route answers 400
 * `use_docs_folder`). What staff do is link the client's folder, on the
 * Client tab.
 *
 * They have no Connect button. The Connectors header counts the Google Docs
 * row once its own read gives a state (a "Connected" row is in "n/total"
 * like any other), and leaves it out while that state is not known.
 */
export const STATUS_ONLY_CONNECTORS: ReadonlySet<ConnectorKey> = new Set<ConnectorKey>(["google_docs"]);

/**
 * The Google Docs catalog row, for a catalog that does not carry one yet (an
 * older backend) and for the fixture businesses. Its `status` is never shown:
 * the Connectors screen reads the Drive connection and this client's folder
 * from the documents service instead (see `docsConnectorView`).
 */
export const GOOGLE_DOCS_CONNECTOR: DataConnector = {
    key: "google_docs",
    label: CONNECTOR_LABEL.google_docs,
    category: "docs",
    blurb: "Reads the client's docs and the comments on them.",
    status: "not_connected",
    // The account is route 1's (`actingAs`) and differs per environment, so the
    // catalogue's own fallback line names none (#3974).
    detail: "Runs as Oura's own Google account",
    lastSync: null,
};

/**
 * What the Google Docs row can say for one client.
 *
 * - `loading`: the first reads have not come back. No status word yet.
 * - `unknown`: the connection or the folder could not be read. Never green.
 * - `not_set_up`: Oura's Google account is not configured (route 1 answers `not_connected`).
 * - `account_action`: the account is configured and broken (route 1 answers `action_needed`).
 * - `not_linked`: this client has no folder yet.
 * - `folder_action`: the folder is linked and its last read failed ("No access" or "Read failed").
 * - `not_read`: linked, first read still to come.
 * - `out_of_date`: the last clean read is older than 3 sync runs, so the reads have stopped.
 * - `connected`: the account works, the folder is linked, and its last read was clean and recent.
 */
export type DocsConnectorState =
    | "loading"
    | "unknown"
    | "not_set_up"
    | "account_action"
    | "not_linked"
    | "folder_action"
    | "not_read"
    | "out_of_date"
    | "connected";

export interface DocsConnectorView {
    state: DocsConnectorState;
    /** The status word and its tone; null while the first reads are out. */
    tag: { label: string; tone: Tone } | null;
    /** One fact per line, in reading order. */
    lines: string[];
    /** The first line says a read failed: announce it. */
    alert: boolean;
    /** Where the row sends staff: link the folder, or open the Client tab's folder card. */
    action: "link_folder" | "open_folder" | null;
    /**
     * The folder in Google Drive, set only when oura@ourteam.ai has no access:
     * the fix is the folder's sharing, which is done in Drive, not in Oura.
     * Absent for every other state, and for a stored link that is not an
     * https Drive URL.
     */
    driveUrl?: string;
}

/** The stored folder link, if it is an https Google Drive URL; null otherwise. */
function driveFolderUrl(raw: string | null | undefined): string | null {
    if (!raw) return null;
    try {
        const u = new URL(raw);
        return u.protocol === "https:" && u.hostname.toLowerCase() === "drive.google.com" ? u.toString() : null;
    } catch {
        return null;
    }
}

/**
 * The row's status word. The folder states take FOLDER_STATUS, the words the
 * Client tab and All clients use; the account states (not set up, action
 * needed, not known) describe Oura's Google account, not a folder.
 */
const DOCS_TAG: Record<DocsConnectorState, DocsConnectorView["tag"]> = {
    loading: null,
    unknown: { label: "Not known", tone: "gray" },
    not_set_up: { label: "Not set up", tone: "gray" },
    account_action: { label: "Action needed", tone: "red" },
    not_linked: FOLDER_STATUS.not_linked,
    // The failed read's own word replaces this: "No access" or "Read failed".
    folder_action: FOLDER_STATUS.read_failed,
    not_read: FOLDER_STATUS.not_read,
    out_of_date: FOLDER_STATUS.out_of_date,
    connected: FOLDER_STATUS.connected,
};

type DocsRead<T> = Pick<DocsLoad<T>, "status" | "data" | "error">;

/**
 * The Google Docs row for one client, from the Drive connection (route 1)
 * and the client's folder (route 3). Pass `folder` null when no client is on
 * screen; the row then speaks for the account alone.
 *
 * A failed read wins over any older answer, so a connection that answered
 * once and then stopped answering reads "Not known", never green. The last
 * good read time stays on the row whenever one is known.
 */
export function docsConnectorView(
    connection: DocsRead<DocsConnection>,
    folder: DocsRead<DocsFolder | null> | null,
    now: number = Date.now(),
): DocsConnectorView {
    const make = (
        state: DocsConnectorState,
        lines: Array<string | null>,
        extra: Partial<Pick<DocsConnectorView, "alert" | "action" | "tag" | "driveUrl">> = {},
    ): DocsConnectorView => ({
        state,
        tag: extra.tag ?? DOCS_TAG[state],
        lines: lines.filter((l): l is string => !!l),
        alert: extra.alert ?? false,
        action: extra.action ?? null,
        ...(extra.driveUrl ? { driveUrl: extra.driveUrl } : {}),
    });
    const lastGood = (iso: string | null | undefined): string | null => {
        const ago = agoText(iso, now);
        return ago ? `Last good read ${ago}.` : null;
    };
    const keptRead = folder?.data?.lastReadAt ?? null;

    if (connection.error) {
        return make("unknown", [docsFailureText(connection.error, "the Google Docs connection"), lastGood(keptRead)], { alert: true });
    }
    const c = connection.data;
    if (!c) return make("loading", []);

    const account = c.actingAs?.trim() || null;
    // Only a number the server sent; an absent interval is left unsaid, never assumed.
    const every = typeof c.syncEveryMinutes === "number" && c.syncEveryMinutes > 0 ? c.syncEveryMinutes : null;
    if (c.status === "not_connected" || !account) return make("not_set_up", ["Oura's Google account isn't set up yet."]);
    if (c.status === "action_needed") {
        // Route 1 puts Google's own reason in `detail`; `lastSyncError` is the last 5-minute run's.
        const reason = c.detail?.trim() || c.lastSyncError?.trim() || "Oura's Google account can't read Drive.";
        return make("account_action", [reason, lastGood(keptRead)]);
    }

    if (!folder) {
        const ago = agoText(c.lastSyncAt, now);
        return make("connected", [`Reads client folders as ${account}.`, ago ? `Last read ${ago}.` : "Not read yet."]);
    }
    if (folder.error) {
        return make("unknown", [docsFailureText(folder.error, "this client's folder"), lastGood(keptRead)], { alert: true });
    }
    if (folder.status !== "ready") return make("loading", []);

    const f = folder.data;
    if (!f) {
        return make(
            "not_linked",
            ["Link the client's Drive folder once.", every ? `Oura then reads it every ${every} minutes as ${account}.` : `Oura then reads it as ${account}.`],
            { action: "link_folder" },
        );
    }
    if (f.lastError) {
        const reason = f.lastError.message?.trim() || "Oura can't read this folder.";
        const status = folderErrorStatus(f.lastError.code);
        const driveUrl = status === "no_access" ? driveFolderUrl(f.url) : null;
        return make("folder_action", [reason, f.lastReadAt ? lastGood(f.lastReadAt) : "Not read yet."], {
            action: "open_folder",
            tag: FOLDER_STATUS[status],
            ...(driveUrl ? { driveUrl } : {}),
        });
    }
    const reads = `Reads this client's folder as ${account}.`;
    const ago = agoText(f.lastReadAt, now);
    if (!f.lastReadAt || !ago) return make("not_read", [reads, "Not read yet."]);
    if (isReadStale(f.lastReadAt, every ?? undefined, now)) {
        return make("out_of_date", [reads, `Last read ${ago}.`, every ? `Oura reads every ${every} minutes.` : null], { action: "open_folder" });
    }
    return make("connected", [reads, `Last read ${ago}.`]);
}

function hashOf(s: string): number {
    return s.split("").reduce((a, c) => (a * 31 + c.charCodeAt(0)) % 9973, 11);
}

/**
 * Deterministic per business so the demo is stable, but varied enough that
 * different businesses show different states. GSC connects first; the site's
 * own platform (here WordPress) is offered connected while rival CMSes stay
 * available-but-not-connected — a business only runs on one.
 *
 * GA4 is connected on every sample: every sample audit carries GA4 sessions
 * (audit.ts), which Search performance shows, and a catalogue saying "not
 * connected" beside them contradicted it. No Google Business Profile: the
 * backend catalogue dropped it (#3801).
 */
export function getConnectors(business: Business): DataConnector[] {
    const h = hashOf(business.id);
    const domain = business.domain.replace(/^https?:\/\//, "");
    const pick = (mod: number, remainder: number, a: ConnectorStatus, b: ConnectorStatus): ConnectorStatus =>
        h % mod === remainder ? a : b;

    const gsc = pick(5, 0, "action_needed", "connected");
    const wp = pick(2, 0, "not_connected", "connected");

    return [
        {
            key: "gsc", label: CONNECTOR_LABEL.gsc, category: "search",
            blurb: "Indexed pages, impressions and coverage.",
            status: gsc,
            detail: gsc === "connected" ? `Property · sc-domain:${domain}` : "Verify domain ownership to finish",
            lastSync: gsc === "connected" ? "2026-07-22" : null,
        },
        {
            key: "ga4", label: CONNECTOR_LABEL.ga4, category: "search",
            blurb: "Organic traffic, sessions and engagement.",
            status: "connected",
            detail: `Property · ${domain}`,
            lastSync: "2026-07-22",
        },
        {
            key: "bing", label: CONNECTOR_LABEL.bing, category: "search",
            blurb: "Bing and Copilot index coverage.",
            status: "not_connected",
            detail: "Add Bing index and impression data",
            lastSync: null,
        },
        {
            key: "wordpress", label: CONNECTOR_LABEL.wordpress, category: "cms",
            blurb: "Publish schema, meta and sitemap fixes.",
            status: wp,
            detail: wp === "connected" ? `${domain} · application password` : "Connect via application password",
            lastSync: wp === "connected" ? "2026-07-20" : null,
        },
        { key: "webflow", label: CONNECTOR_LABEL.webflow, category: "cms", blurb: "Publish fixes to a Webflow site.", status: "not_connected", detail: "Connect a Webflow site", lastSync: null },
        { key: "wix", label: CONNECTOR_LABEL.wix, category: "cms", blurb: "Publish fixes to a Wix site.", status: "not_connected", detail: "Connect a Wix site", lastSync: null },
        { key: "shopify", label: CONNECTOR_LABEL.shopify, category: "cms", blurb: "Publish fixes to a Shopify store.", status: "not_connected", detail: "Connect a Shopify store", lastSync: null },
        { key: "squarespace", label: CONNECTOR_LABEL.squarespace, category: "cms", blurb: "Publish fixes to a Squarespace site.", status: "not_connected", detail: "Connect a Squarespace site", lastSync: null },
        // Status only: the screen reads its state from the documents sample store.
        { ...GOOGLE_DOCS_CONNECTOR },
    ];
}
